import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';

import { ServiceManagerBase } from '../common/service-manager-base';
import { BroadcastService } from '../../core/services/broadcast.service';

import { Album } from './album.model';
import { AlbumApiService } from './album-api.service';
import { AlbumEditModel } from './album-edit.model';
import { DeletedAlbum } from './deleted-album.model';
import { ImageManagerService, UploadFileResult } from '../images/image-manager.service';
import { ImageAsset } from '../images/image-asset-model';
import { MyCampApp } from '../common/enumerations';

@Injectable({
    providedIn: 'root'
})
export class AlbumManagerService extends ServiceManagerBase
{
    constructor(private albumApiService: AlbumApiService,
                protected override broadcastService: BroadcastService,
                private imageManager: ImageManagerService)
    {
        super(broadcastService);
    }

    clearCache()
    {
    }

    // Note: parentAlbumId = 0 for top-level
    getAlbum(id: number): Observable<Album>
    {
        return this.albumApiService.getAlbum(id);
    }

    saveAlbum(albumEditModel: AlbumEditModel, removeThumbnail: boolean, file?: File): Observable<Album>
    {
        return new Observable<Album>(observer =>
        {
            this.imageManager.uploadFile(file, true).subscribe({
                next: (photoResult: UploadFileResult) =>
                {
                    this.saveAlbumInternal(albumEditModel, removeThumbnail, photoResult.fileOrUrl).subscribe({
                        next: (savedItem: any) =>
                        {
                            observer.next(savedItem);
                            observer.complete();
                        },
                        error: error => observer.error(error)
                    });
                },
                error: error => observer.error(error)
            });
        });
    }

    private saveAlbumInternal(albumEditModel: AlbumEditModel, removeThumbnail: boolean, sasToken?: string): Observable<Album>
    {
        return new Observable<Album>(observer =>
        {
            if (albumEditModel.Id === 0)
            {
                return this.albumApiService.createAlbum(albumEditModel).subscribe({
                    next: (createdAlbum: Album) =>
                    {
                        albumEditModel.Id = createdAlbum.Id;
                        this.setImageForAlbum(albumEditModel, removeThumbnail, sasToken).subscribe({
                            next: (imageAsset: ImageAsset | undefined) =>
                            {
                                createdAlbum.Photo = imageAsset || createdAlbum.Photo;
                                createdAlbum.PhotoAutoAssigned = createdAlbum.Photo == null;
                                observer.next(createdAlbum);
                                observer.complete();
                            },
                            error: error => observer.error(error)
                        });
                    },
                    error: error => observer.error(error)
                });
            }

            return this.albumApiService.updateAlbum(albumEditModel).subscribe({
                next: (updatedAlbum: Album) =>
                {
                    this.setImageForAlbum(albumEditModel, removeThumbnail, sasToken).subscribe({
                        next: (imageAsset: ImageAsset | undefined) =>
                        {
                            updatedAlbum.Photo = removeThumbnail ? undefined : (imageAsset || updatedAlbum.Photo);
                            updatedAlbum.PhotoAutoAssigned = updatedAlbum.Photo == null;
                            observer.next(updatedAlbum);
                            observer.complete();
                        },
                        error: error => observer.error(error)
                    });
                },
                error: error => observer.error(error)
            });
        });
    }

    addMediaToAlbum(album: Album, type: MyCampApp.AssetType, uploadResult: UploadFileResult)
    {
        return this.albumApiService.addMediaToAlbum(album.Id, type, uploadResult);
    }

    deleteMediaFromAlbum(album: Album, imageAssetIds: number[])
    {
        return this.albumApiService.deleteMediaFromAlbum(album.Id, imageAssetIds);
    }

    moveAlbum(albumId: number, fromParentId: number, fromChildAlbumIds: number[], toParentId: number, toChildAlbumIds: number[]): Observable<Album>
    {
        return this.albumApiService.moveAlbum(albumId, fromParentId, fromChildAlbumIds, toParentId, toChildAlbumIds);
    }

    deleteAlbum(album: Album): Observable<any>
    {
        return this.albumApiService.deleteAlbum(album);
    }

    getDeletedAlbums(): Observable<DeletedAlbum[]>
    {
        return this.albumApiService.getDeletedAlbums();
    }

    restoreAlbums(albumIds: number[]): Observable<any>
    {
        return this.albumApiService.restoreAlbums(albumIds);
    }

    duplicateAlbum(album: Album, newAlbumName: string): Observable<Album>
    {
        return this.albumApiService.duplicateAlbum(album, newAlbumName);
    }

    setImageForAlbum(albumEditModel: AlbumEditModel, removeThumbnail: boolean, uploadedImageUrl?: string): Observable<ImageAsset | undefined>
    {
        // Possibly adding/replacing image
        if (!removeThumbnail && uploadedImageUrl != null) { return this.albumApiService.setImageForAlbum(albumEditModel.Id, uploadedImageUrl); }

        // See if the image is being removed
        if (removeThumbnail) return this.albumApiService.deleteImageForAlbum(albumEditModel.Id);

        return of(undefined);
    }

    moveMediaToAlbum(fromAlbum: Album, toAlbum: Album, imageAssetIds: number[])
    {
        return this.albumApiService.moveMediaToAlbum(fromAlbum.Id, toAlbum.Id, imageAssetIds);
    }
}
